<template>
  <v-app-bar app color="#635347" class="app-bar" height="56px">
    <!-- Hamburger menu icon for mobile -->
    <v-app-bar-nav-icon @click="drawer = !drawer" class="d-md-none"></v-app-bar-nav-icon>

    <!-- Logo Container with click event -->
    <v-container class="logo-container">
      <v-img
        :src="isMobile ? `${apiBaseUrl}/img/bbas-logo-1.png` : `${apiBaseUrl}/img/bbas-logo-1.png`"
        :max-width="isMobile ? '100px' : '138px'"
        :max-height="isMobile ? '100px' : '138px'"
        class="logo-image"
        @click="redirectToHome"
        style="cursor: pointer;"
      ></v-img>
      <div class="logo-text" v-html="isMobile ? 'BBAS' : 'Bail Bond<br>Agent Services'"></div>
    </v-container>

    <v-spacer></v-spacer>

    <!-- Desktop Menu Buttons Container -->
    <div class="desktop-menu-container" v-if="!isMobile">
      <div class="menu-buttons-wrapper">
        <v-btn v-if="shouldShowOwnerMenu" :to="{ name: 'OwnerAccount' }">Owner Account</v-btn>
        <v-btn v-if="shouldShowCompanyMenu" :to="{ name: 'CompanyAccount' }">Company Account</v-btn>
        <v-btn v-if="shouldShowUserMenu" :to="{ name: 'UserAccount' }">User Account</v-btn>
        <v-btn v-if="shouldShowFormsMenu" :to="{ name: 'Forms' }">Forms</v-btn>
        <v-btn v-if="shouldShowSettingsMenu" :to="{ name: 'Settings' }">Settings</v-btn>
        <v-btn v-if="isUserLoggedIn" @click="handleLogout">Log Out</v-btn>
        <div style="width: 100%"></div>
        <v-btn class="operational-btn" v-if="shouldShowOperationalMenu" :to="{ name: 'VoiceMail' }">Voice Calls</v-btn>
        <v-btn class="operational-btn" v-if="shouldShowOperationalMenu" :to="{ name: 'IntakeForms' }">Jail Calls</v-btn>
      </div>
    </div>

    <!-- Display user name -->
    <div class="user-name" v-if="userName">
      {{ userName }}
    </div>

    <!-- Login Component for Desktop -->
    <div class="login-container" v-if="!isUserLoggedIn && !isMobile">
      <LoginComponent />
    </div>

    <!-- Mobile Navigation Drawer -->
    <v-navigation-drawer v-model="drawer" app class="d-md-none custom-drawer" :temporary="true">
      <v-list>
        <v-list-item v-if="shouldShowOwnerMenu" :to="{ name: 'OwnerAccount' }">Owner Account</v-list-item>
        <v-list-item v-if="shouldShowCompanyMenu" :to="{ name: 'CompanyAccount' }">Company Account</v-list-item>
        <v-list-item v-if="shouldShowUserMenu" :to="{ name: 'UserAccount' }">User Account</v-list-item>
        <v-list-item v-if="shouldShowFormsMenu" :to="{ name: 'Forms' }">Forms</v-list-item>
        <v-list-item v-if="shouldShowSettingsMenu" :to="{ name: 'Settings' }">Settings</v-list-item>
        <v-list-item v-if="shouldShowOperationalMenu" :to="{ name: 'VoiceMail' }">Voice Calls</v-list-item>
        <v-list-item v-if="shouldShowOperationalMenu" :to="{ name: 'IntakeForms' }">Jail Calls</v-list-item>
        <v-list-item v-if="isUserLoggedIn" @click="handleLogout">Log Out</v-list-item>
      </v-list>

      <!-- Login Component for Mobile -->
      <div class="login-container" v-if="!isUserLoggedIn">
        <LoginComponent />
      </div>
    </v-navigation-drawer>

  </v-app-bar>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import LoginComponent from './LoginComponent.vue';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';

export default {
  name: 'MenuComponent',
  components: {
    LoginComponent,
  },
  data() {
    return {
      drawer: false,
      apiBaseUrl: process.env.VUE_APP_THIS_SERVER
    };
  },
  computed: {
    ...mapGetters([
      'isUserLoggedIn',
      'userRole',
      'getSelectedCompany'
    ]),
    
    // User name computed property
    userName() {
      return this.$store.state.user?.name || '';
    },

    // Menu visibility computed properties
    shouldShowOwnerMenu() {
      return this.isUserLoggedIn && this.userRole === 'Owner';
    },

    shouldShowCompanyMenu() {
      return this.isUserLoggedIn && ['Owner', 'Administrator'].includes(this.userRole);
    },

    shouldShowUserMenu() {
      return this.isUserLoggedIn && this.userRole;
    },

    shouldShowSettingsMenu() {
      return this.isUserLoggedIn && ['Owner', 'Administrator'].includes(this.userRole);
    },

    shouldShowOperationalMenu() {
      return this.isUserLoggedIn && ['Owner', 'Administrator', 'User'].includes(this.userRole);
    },

    shouldShowFormsMenu() {
      return this.isUserLoggedIn &&
        [1002, 1008].includes(this.getSelectedCompany?.companyNumber);
    },

    isMobile() {
      return window.innerWidth <= 960;
    },
  },
  
  methods: {
    ...mapActions(['logout']),

    async handleLogout() {
      try {
        await signOut(auth);
        // Use the simplified logout action
        await this.logout();
        await this.$router.push({ name: 'LoginPage' });
      } catch (error) {
        console.error('Error logging out:', error.message);
        // You might want to add error handling UI here
      }
    },

    redirectToHome() {
      window.location.href = 'https://BailBondAgentServices.com';
    },
  },
};
</script>

<style scoped>

.operational-btn {
  border: 2px solid #d2b48c !important;
  font-weight: bold;
  margin-left: 12px !important;
}

.operational-btn.v-btn--active {
  background-color: #d2b48c !important;
  color: #635347 !important;
  border: 2px solid #ffffff !important;
}

.user-name {
  position: absolute;
  right: 20px;
  top: 10px;
  font-size: 0.8em;
  font-weight: bold;
  color: #d2b48c;
}

.app-bar {
  position: relative !important;
  padding: 5px;
  min-height: 70px;
}

.logo-container {
  display: flex;
  align-items: center;
  margin-left: 10px;
  flex-shrink: 0;
  max-width: fit-content;
  padding: 0;
}

.logo-image {
  width: 100%;
  height: auto;
}

.logo-text {
  color: white;
  font-size: 24px;
  margin-left: 10px;
  white-space: normal;
  text-align: left;
}

.desktop-menu-container {
  display: flex;
  flex-grow: 1;
  margin: 0 10px;
  overflow-y: auto;
  max-height: 100%;
}

.menu-buttons-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  align-items: center;
  align-content: flex-start;
  width: 100%;
  margin-top: 24px;
}

.login-container {
  min-width: 480px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.v-btn {
  margin: 0;
  color: white;
}

.logout-button {
  background-color: #d2b48c;
  color: white;
  border-radius: 10px;
}

.custom-drawer {
  height: 40vh !important;
  top: 15vh !important;
}

.desktop-menu-container {
  display: flex;
  flex-grow: 0;  
  margin: 0 10px;
  overflow-y: auto;
  max-height: 100%;
  justify-content: flex-end; 
}

.menu-buttons-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  align-items: center;
  align-content: flex-start;
  justify-content: flex-end; 
  width: auto;  
}

.logo-container {
  display: flex;
  align-items: center;
  margin-left: 10px;
  flex-shrink: 0;
  max-width: fit-content;
  padding: 0;
  margin-right: 20px;  
}

.desktop-menu-container {
  display: flex;
  flex-grow: 0;
  margin: 0 10px;
  overflow-y: auto;
  max-height: 100%;
  justify-content: flex-end;
  max-width: 60%;  /* Added this */
}

.menu-buttons-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  align-items: center;
  align-content: flex-start;
  justify-content: flex-end;
  width: auto;
}

.logo-text {
  color: white;
  font-size: 24px;
  margin-left: 10px;
  white-space: nowrap;
  text-align: left;
}

@media (max-width: 960px) {
  .login-container {
    min-width: 300px;
  }
  .logo-text {
    font-size: 18px;
  }
  .app-bar {
    min-height: 56px;
  }
  .logout-button {
    margin-right: 0;
  }
}
</style>
